"use client";

/** REMOVED FROM THE ACTIVE ORGANISATION (VC-Authz, 2026-10-19). When the
 *  organisation a session acts as stops holding the user, the server rebinds
 *  the session on the next /auth/user and says so ONCE:
 *  `active_org_removed: {id, name}`, with `organization_id` already the new
 *  organisation (null when the user holds none any more). Before, the page
 *  waited forever on "Loading...".
 *
 *  - "You were removed from <name>" shows until dismissed. It is kept for the
 *    tab's session, because the next /auth/user (the 60 s watchdog) no longer
 *    carries the flag.
 *  - Removed, with no organisation left: a page saying so, never a wait for a
 *    workspace. Only then. A session acting as no organisation for any other
 *    reason (platform groups only) is left as it is.
 *  - Not removed but locked (plan 81, VC-Authz 30 Oct): the organisation is a
 *    company's, open only in a session signed in through that company, and
 *    this one wasn't. The server rebinds alike and adds `opens_with`; the
 *    words are then "opens only when you sign in with Microsoft", with that
 *    sign-in, never "you were removed". */

import React, { useEffect, useState } from "react";
import { companySignInUrl, providerWords, type CompanyProvider } from "@sourcepilot/ui/framework/data/company-org";

const KEY = "sp-org-removed";

type Removed = { id: string; name: string | null; opens_with?: CompanyProvider };
type AuthUser = { active_org_removed?: Removed | null; organization_id?: string | null } | null | undefined;

function readKept(): Removed | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Removed) : null;
  } catch {
    return null;
  }
}

function keep(r: Removed | null): void {
  try {
    if (r) sessionStorage.setItem(KEY, JSON.stringify(r));
    else sessionStorage.removeItem(KEY);
  } catch { /* no storage: shown for this render only */ }
}

/** What the removal leaves the page with: the notice to show (until
 *  dismissed), and whether no organisation is left at all. */
export function useOrgRemoved(user: AuthUser): { removed: Removed | null; noOrganisation: boolean; dismiss: () => void } {
  const [removed, setRemoved] = useState<Removed | null>(() => readKept());
  const flag = user?.active_org_removed;
  useEffect(() => {
    if (flag && typeof flag.id === "string") {
      const r: Removed = { id: flag.id, name: flag.name ?? null };
      if (flag.opens_with === "microsoft" || flag.opens_with === "google") r.opens_with = flag.opens_with;
      keep(r);
      setRemoved(r);
    }
  }, [flag?.id, flag?.name, flag?.opens_with]); // eslint-disable-line react-hooks/exhaustive-deps
  const noOrganisation = !!removed && !!user && (user.organization_id ?? null) === null;
  return {
    removed,
    noOrganisation,
    dismiss: () => { keep(null); setRemoved(null); },
  };
}

/** The company sign-in that opens it, back on this page. */
function signInHere(provider: CompanyProvider): string {
  const here = typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : null;
  return companySignInUrl(provider, { returnTo: here });
}

export function OrgRemovedNotice({ removed, onDismiss }: { removed: Removed | null; onDismiss: () => void }) {
  if (!removed) return null;
  const locked = removed.opens_with;
  return (
    <div role="status" data-testid="org-removed-notice"
      className="flex items-center justify-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
      {locked ? (
        <>
          <span>{removed.name ?? "This organisation"} opens only when you sign in with {providerWords(locked)}.</span>
          <a className="rounded border border-amber-300 bg-white px-2 py-0.5 text-xs hover:bg-amber-100"
            href={signInHere(locked)} data-testid="org-locked-sign-in">
            Sign in with {providerWords(locked)}
          </a>
        </>
      ) : (
        <span>You were removed from {removed.name ?? "an organisation"}.</span>
      )}
      <button className="rounded border border-amber-300 bg-white px-2 py-0.5 text-xs hover:bg-amber-100"
        onClick={onDismiss} data-testid="org-removed-dismiss">
        Dismiss
      </button>
    </div>
  );
}

export function NoOrganisation({ removed }: { removed?: Removed | null }) {
  const locked = removed?.opens_with;
  if (locked) {
    return (
      <div className="flex h-full flex-1 items-center justify-center p-8" data-testid="no-organisation-open">
        <div className="max-w-md space-y-3 text-center">
          <h1 className="text-lg font-semibold text-slate-800">Your organisations open with your work sign-in</h1>
          <p className="text-sm text-slate-600">
            {removed?.name ?? "Your organisation"} opens only when you sign in with your {providerWords(locked)} work account.
          </p>
          <a className="inline-block rounded border px-3 py-1 text-sm text-slate-700 hover:bg-slate-50"
            href={signInHere(locked)} data-testid="no-organisation-sign-in">
            Sign in with {providerWords(locked)}
          </a>
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-1 items-center justify-center p-8" data-testid="no-organisation">
      <div className="max-w-md space-y-2 text-center">
        <h1 className="text-lg font-semibold text-slate-800">You don't belong to any organisation now</h1>
        <p className="text-sm text-slate-600">
          Ask someone in an organisation to invite you. Your invitations show on your profile.
        </p>
      </div>
    </div>
  );
}
